import {
    ProgressBar,
    type Segment,
} from "@design-system/DataDisplay/ProgressBar/ProgressBar";
import { Icon } from "@design-system/Icon";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Text } from "@design-system/Typography/Text";
import chroma from "chroma-js";
import { type TFunction } from "i18next";
import { useMemo, type FC } from "react";
import { useQuestionnaireStatsTranslations } from "./i18n";

export type TChartData = {
    title?: string | null;
    seriesKeys: string[];
    dataRows: Record<string, string | number>[];
    seriesConfig?:
        | ({ __typename?: string } & Record<
              string,
              { color?: string | null; label?: string | null }
          >)
        | null;
    chartMetadata?: {
        totalQuestions?: number | null;
        userIds?: string[] | null;
        totalQuestionsByIdx?: number[] | null;
    } | null;
};

export const CompletionChart: FC<{
    chartData: TChartData;
}> = ({ chartData }) => {
    const { title, seriesKeys, dataRows, seriesConfig, chartMetadata } =
        chartData;
    const { t, keys } = useQuestionnaireStatsTranslations();
    const segments: Segment[] = useMemo(() => {
        return makeCompletionSegments({
            dataRows,
            seriesKeys,
            seriesConfig,
            chartMetadata,
            t,
            keys,
        });
    }, [seriesKeys, dataRows, seriesConfig, chartMetadata]);
    return (
        <FlexCol className="flex-1 max-w-[600px]" gap="2">
            <FlexRow justifyContent="between">
                <Text variant="caption-bold">
                    {t(keys.completion_chart_title, {
                        context: title,
                    })}
                </Text>
                <Text variant="caption-bold">
                    {t(keys.completion_chart_label_total, {
                        count: chartMetadata?.totalQuestions ?? 0,
                    })}
                </Text>
            </FlexRow>
            <FlexCol gap="1.5">
                <ProgressBar
                    className="flex-1"
                    segments={segments}
                    hideOnZeroTotal={false}
                />
                <FlexRow gap="4">
                    {segments.map((segment) => (
                        <FlexRow
                            alignItems="center"
                            key={segment.label}
                            gap="1"
                        >
                            <Icon
                                name="circleBig"
                                size="xs"
                                style={{ color: segment.color?.css() }}
                            />
                            <Text variant="body-sm">
                                <Text as="span" variant="body-sm-bold">
                                    {segment.value}{" "}
                                </Text>
                                <Text as="span" color="secondary">
                                    {segment.label}
                                </Text>
                            </Text>
                        </FlexRow>
                    ))}
                </FlexRow>
            </FlexCol>
        </FlexCol>
    );
};

export function makeCompletionSegments({
    dataRows,
    seriesKeys,
    seriesConfig,
    chartMetadata,
    t,
    keys,
}: TChartData & {
    t: TFunction;
    keys: Record<string, string>;
}) {
    const row = dataRows[0];
    return seriesKeys.map((key) => {
        const value = row[key];
        const config = seriesConfig?.[key];
        return {
            value: typeof value === "number" ? value : 0,
            color: chroma(config?.color ?? ""),
            label: t(keys.completion_chart_label, {
                context: config?.label,
            }),
            labelValue: `${value} / ${chartMetadata?.totalQuestions}`,
        };
    });
}
